import { Fragment, useState } from "react";
import { useLayer } from "@/lib/layer-stack";

import { Badge } from "@/components/Badge";
import { I } from "@/components/Icon";

import { CPP_DEFAULT_COLS, cppColLabel, cppColValue, cppFilterCols } from "./columns";
import type { CppGroupBlock, CppMetric, CppTestDetail } from "./types";

const fmtInt = (n: number) => Math.round(n).toLocaleString("en-US");

function cppFact(metric: CppMetric, fact: number | null): string {
  if (fact == null) return "—";
  if (metric === "ipm") return fact.toFixed(2); // per-mille, bare (platform convention)
  if (metric === "cpi" || metric === "cpa") return "$" + fact.toFixed(2);
  if (metric === "roas") return fact.toFixed(2) + "×";
  return String(fact);
}

// Collapsed variant row: preview of the first three screenshots as small
// thumbnails (real App Store images, no decorative notch/strip). Falls
// back to neutral placeholders while screenshots are still loading.
function CppMiniShots({ shots, live }: { shots: string[]; live: boolean }) {
  const items = shots.slice(0, 3);
  return (
    <span className="cpp-minishots">
      {items.length > 0
        ? items.map((url, i) => (
            <span key={i} className="cpp-minishot">
              <img src={url} alt="" loading="lazy" />
            </span>
          ))
        : [0, 1, 2].map((i) => <span key={i} className="cpp-minishot ph" />)}
      {live && (
        <span className="cpp-thumb-cur" title="Current live page">
          live
        </span>
      )}
    </span>
  );
}

function ConfidenceBar({
  lo,
  hi,
  kind,
  vsName,
}: {
  // The interval lives on the log of the ratio, so its bounds are asymmetric
  // around the point estimate — the backend sends them; never rebuild them as
  // diff ± ci (that is what printed the impossible "−112%").
  lo: number;
  hi: number;
  kind: string;
  vsName?: string;
}) {
  const crosses = lo < 0 && hi > 0;
  const allPos = lo >= 0;
  const allNeg = hi <= 0;
  const zeroPos =
    hi === lo ? 50 : Math.max(0, Math.min(100, ((0 - lo) / (hi - lo)) * 100));
  const fmt = (v: number) =>
    (v > 0 ? "+" : v < 0 ? "−" : "") + Math.abs(v).toFixed(1) + "%";
  const negW = crosses ? zeroPos : allNeg ? 100 : 0;
  const posL = crosses ? zeroPos : 0;
  const posW = crosses ? 100 - zeroPos : allPos ? 100 : 0;
  return (
    <div className={"cbar " + kind} title={vsName ? `vs ${vsName}` : undefined}>
      <span className={"cbar-bound " + (lo < 0 ? "neg" : "pos")}>{fmt(lo)}</span>
      <div className="cbar-track">
        {negW > 0 && (
          <span className="cbar-seg neg" style={{ left: 0, width: negW + "%" }} />
        )}
        {posW > 0 && (
          <span className="cbar-seg pos" style={{ left: posL + "%", width: posW + "%" }} />
        )}
        {crosses && (
          <span className="cbar-zero2" style={{ left: zeroPos + "%" }}>
            <i className="cbar-diamond" />
          </span>
        )}
      </div>
      <span className={"cbar-bound " + (hi > 0 ? "pos" : "neg")}>{fmt(hi)}</span>
    </div>
  );
}

/** §4.1 — a group is in exactly one of three states, computed server-side:
 *  collecting (gate not cleared), in_progress (bars, no verdict),
 *  winner_found (a winner survived two consecutive daily verdicts). */
function StatePill({ state }: { state: string }) {
  const map: Record<string, { cls: "enabled" | "accent" | "warn" | "paused"; label: string }> = {
    winner_found: { cls: "enabled", label: "Winner found" },
    in_progress: { cls: "warn", label: "In progress" },
    collecting: { cls: "paused", label: "Collecting data" },
  };
  const c = map[state] || map.collecting;
  return (
    <Badge kind={c.cls}>
      {state === "collecting" && <span className="cpp-pulse" />}
      {c.label}
    </Badge>
  );
}

function StateNote({ state, frozen }: { state: string; frozen?: boolean }) {
  if (state === "winner_found")
    return (
      <span className="cpp-note pos">
        {I.check} Confirmed winner above threshold at the chosen confidence.
      </span>
    );
  if (state === "in_progress")
    return (
      <span className="cpp-note">
        Comparing — no variant has separated from the others yet.
      </span>
    );
  return (
    <span className="cpp-note">
      {frozen ? "Final figures." : "Collecting data — not enough yet to compare."}
    </span>
  );
}

/** §4.4 — the daily verdict job stores the forecast; we only render it. */
function ForecastLine({
  status,
  days,
  note,
  confirmedAt,
}: {
  status: string;
  days: number | null;
  note: string | null;
  confirmedAt: string | null;
}) {
  if (status === "winner_found") {
    if (!confirmedAt) return null;
    return (
      <span className="cpp-eta">
        {I.clock}Winner confirmed {new Date(confirmedAt).toLocaleDateString()}
      </span>
    );
  }
  if (note === "estimating")
    return <span className="cpp-eta">{I.clock}Estimating…</span>;
  if (note === "variants_close")
    return (
      <span className="cpp-eta">
        {I.clock}Variants are close — no decision estimate yet
      </span>
    );
  if (days == null) return null;
  const label = days > 90 ? "90+" : String(days);
  return (
    <span className="cpp-eta">
      {I.clock}~{label} d {status === "collecting" ? "until first results" : "to decision"}
    </span>
  );
}

function CppScreens({
  name,
  shots,
}: {
  name: string;
  shots: string[];
}) {
  return (
    <div className="cpp-screens">
      <div className="cpp-screens-head">
        <span className="muted">Custom Product Page preview</span>
        <span className="cpp-screens-name">{name}</span>
      </div>
      <div className="cpp-screens-row">
        {shots.length === 0 ? (
          <span className="muted" style={{ fontSize: 12, padding: "8px 0" }}>
            No screenshots cached for this page yet.
          </span>
        ) : (
          shots.slice(0, 3).map((url, i) => (
            <div key={i} className="cppt-shot" style={{ "--tc": "#3a3f52" } as React.CSSProperties}>
              <img
                src={url}
                alt={`Screenshot ${i + 1}`}
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// --- results table (the key component) -------------------------------------

export function ResultsTable({
  test,
  group,
  frozen,
  canEdit,
  cols,
  screenshotsByPpid,
  onApply,
}: {
  test: CppTestDetail;
  group: CppGroupBlock;
  frozen?: boolean;
  canEdit: boolean;
  cols: string[];
  screenshotsByPpid: Map<string, string[]>;
  onApply: (variantId: number) => void;
}) {
  const [open, setOpen] = useState<number | null>(null);
  // Filter to currently-offered keys so stale columns from old saved
  // presets / localStorage (e.g. removed tap/view splits) never render.
  const extra = cppFilterCols(cols.length ? cols : CPP_DEFAULT_COLS);
  const span = 4 + extra.length + 2;
  const totalImpr = group.variants.reduce((s, v) => s + v.impressions, 0) || 1;
  const groupState = group.display_status;
  const [pendingApply, setPendingApply] = useState<number | null>(null);
  // §4.2: applying a confirmed winner is the expected action; applying
  // anything else — or anything at all before a verdict — needs an
  // acknowledgement that the data doesn't support the choice yet.
  const isConfirmedWinner = (v: { is_winner: boolean }) =>
    groupState === "winner_found" && v.is_winner;
  const requestApply = (variantId: number) => {
    const v = group.variants.find((x) => x.variant_id === variantId);
    if (v && isConfirmedWinner(v)) onApply(variantId);
    else setPendingApply(variantId);
  };
  const refName = (id: number | null) => {
    if (id == null) return undefined;
    const r = group.variants.find((x) => x.variant_id === id);
    if (!r) return undefined;
    return r.display_name || (r.is_default_page ? "Default Product Page" : "Custom Product Page");
  };

  return (
    <div className="cpp-rt-wrap">
      <table className="cpp-rt">
        <thead>
          <tr>
            <th className="col-var">Variant</th>
            <th className="num">Impr. share</th>
            <th className="num">Fact · {test.metric.toUpperCase()}</th>
            <th className="num">Scaled to 100%</th>
            {extra.map((key) => (
              <th key={key} className="num" title={cppColLabel(key)}>
                {cppColLabel(key)}
              </th>
            ))}
            <th
              className="col-conf"
              title={
                test.comparison_mode === "vs_default"
                  ? "Compared with the default product page"
                  : "Every variant compared with every other"
              }
            >
              Performance ({test.confidence_level}% confidence interval)
            </th>
            <th className="col-act" />
          </tr>
        </thead>
        <tbody>
          {group.variants.map((v) => {
            // §4.1: nothing may read as a win before the group's verdict is
            // confirmed. `is_winner` on the snapshot means "won THIS daily
            // run" — the engine writes it on the first crossing and
            // `_confirmed_twice` counts it, so it stays as is in the DB; the
            // UI only celebrates once two runs have agreed.
            const win = isConfirmedWinner(v);
            const st = win ? "winner" : v.state === "winner" ? "nodiff" : v.state;
            const isOpen = open === v.variant_id;
            // On a finished card, "Applied" is about which variant we actually
            // pushed to ASA — an early apply never had a verdict to win.
            const applied = group.winner_variant_id === v.variant_id;
            const sharePct =
              v.impressions > 0
                ? (v.impressions / totalImpr) * 100
                : v.target_split_pct;
            const projected =
              v.fact != null && test.metric === "ipm"
                ? v.fact
                : v.installs / Math.max(v.target_split_pct / 100, 0.01);
            const shots = v.product_page_id
              ? screenshotsByPpid.get(v.product_page_id) || []
              : screenshotsByPpid.get("default") || [];
            return (
              <Fragment key={v.variant_id}>
                <tr className={"cpp-row " + st + (isOpen ? " is-open" : "")}>
                  <td className="col-var">
                    <button
                      className={"cpp-var" + (isOpen ? " open" : "")}
                      onClick={() => setOpen(isOpen ? null : v.variant_id)}
                    >
                      <span className={"twist" + (isOpen ? " open" : "")}>{I.chevron}</span>
                      <CppMiniShots shots={shots} live={v.is_default_page} />
                      <span className="cpp-var-meta">
                        <span className="cpp-var-name">
                          {v.display_name || (v.is_default_page ? "Default Product Page" : "Custom Product Page")}
                          {win && (
                            <span className="cpp-win-chip">{I.check} Winner</span>
                          )}
                        </span>
                        <span className="cpp-var-sub">
                          {v.is_default_page ? "Default product page" : "Custom Product Page"}
                          {v.awaiting_delivery ? " · awaiting first serve" : ""}
                        </span>
                      </span>
                    </button>
                  </td>
                  <td className="num">{sharePct.toFixed(0)}%</td>
                  <td className="num strong">
                    {v.awaiting_delivery ? (
                      <span className="muted" title="Waiting for the first impression">—</span>
                    ) : (
                      cppFact(test.metric, v.fact)
                    )}
                  </td>
                  <td className="num">
                    {/* §3.5: "scaled to 100%" projects the arm's raw numbers up
                        to a full traffic share — a comparative claim. Below the
                        display gate it must stay hidden along with the bars. */}
                    {groupState === "collecting" ? (
                      <span className="muted">—</span>
                    ) : test.metric === "ipm"
                      ? cppFact(test.metric, v.fact)
                      : (
                        <>
                          {fmtInt(projected)}
                          <span className="cpp-unit"> inst.</span>
                        </>
                      )}
                  </td>
                  {extra.map((key) => (
                    <td key={key} className="num">
                      {cppColValue(v, key)}
                    </td>
                  ))}
                  <td className="col-conf">
                    {v.is_default_page && v.diff_pct == null ? (
                      <span className="cpp-baseline-chip">Baseline</span>
                    ) : v.diff_pct == null || v.ci_low == null || v.ci_high == null ? (
                      <span className="muted" style={{ fontSize: 12 }}>collecting…</span>
                    ) : (
                      <div className="cpp-conf-cell">
                        <ConfidenceBar
                          lo={v.ci_low}
                          hi={v.ci_high}
                          kind={st}
                          vsName={refName(v.reference_variant_id)}
                        />
                      </div>
                    )}
                  </td>
                  <td className="col-act">
                    {frozen ? (
                      applied ? (
                        <span className="cpp-applied">{I.check} Applied</span>
                      ) : (
                        <span className="muted" style={{ fontSize: 12 }}>—</span>
                      )
                    ) : (
                      <button
                        className={"btn sm " + (win ? "primary" : "")}
                        disabled={!canEdit || !v.ad_id}
                        onClick={() => requestApply(v.variant_id)}
                      >
                        {win ? "Apply winner" : "Apply"}
                      </button>
                    )}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="cpp-preview-row">
                    <td colSpan={span}>
                      <CppScreens
                        name={v.display_name || (v.is_default_page ? "Default Product Page" : "CPP")}
                        shots={shots}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <div className="cpp-rt-foot">
        {!frozen && (
          <ForecastLine
            status={groupState}
            days={group.forecast_days}
            note={group.forecast_note}
            confirmedAt={group.winner_confirmed_at}
          />
        )}
        <StateNote state={groupState} frozen={frozen} />
      </div>
      {pendingApply != null && (
        <ConfirmEarlyApply
          onCancel={() => setPendingApply(null)}
          onConfirm={() => {
            onApply(pendingApply);
            setPendingApply(null);
          }}
        />
      )}
    </div>
  );
}

function ConfirmEarlyApply({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useLayer(true, onCancel);
  return (
    <div className="cs-overlay" onClick={onCancel}>
      <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Apply this variant early?</h3>
        <p className="muted">
          No winner has been determined — there is not enough data for a
          statistically reliable conclusion. Apply this variant anyway?
        </p>
        <div className="cs-modal-foot">
          <button className="btn sm" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn sm primary" onClick={onConfirm}>
            Apply anyway
          </button>
        </div>
      </div>
    </div>
  );
}

export function GroupBlock({
  test,
  group,
  frozen,
  canEdit,
  cols,
  screenshotsByPpid,
  onApply,
}: {
  test: CppTestDetail;
  group: CppGroupBlock;
  frozen?: boolean;
  canEdit: boolean;
  cols: string[];
  screenshotsByPpid: Map<string, string[]>;
  onApply: (variantId: number) => void;
}) {
  const groupState = group.display_status;
  return (
    <div className="cpp-group">
      <div className="cpp-group-head">
        <span className="cpp-group-ico">{I.grid}</span>
        <h4>{group.ad_group_name || `Ad group #${group.ad_group_id}`}</h4>
        <span className="cpp-group-meta">{group.variants.length} pages</span>
        <span style={{ marginLeft: "auto" }}>
          <StatePill state={groupState} />
        </span>
      </div>
      {group.status_reason && groupState !== "winner_found" && (
        <div className="cpp-group-reason">{group.status_reason}</div>
      )}
      <ResultsTable
        test={test}
        group={group}
        frozen={frozen}
        canEdit={canEdit}
        cols={cols}
        screenshotsByPpid={screenshotsByPpid}
        onApply={onApply}
      />
    </div>
  );
}

export { StatePill };
